import React, { useState } from 'react';
import { X, Check, CreditCard, Sparkles, ShieldCheck, Zap, Building, Lock, ArrowRight, RefreshCw, AlertCircle } from 'lucide-react';
import { Language, Jurisdiction, Currency } from '../utils/i18n';

export interface RegionalPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId?: string;
  agentName?: string;
  region?: Jurisdiction;
  jurisdiction?: Jurisdiction;
  language?: Language;
  currency?: Currency;
  defaultPrice?: number;
  initialPrice?: number;
  defaultSinglePrice?: number;
  initialSinglePrice?: number;
  userEmail?: string;
  currentUserEmail?: string;
  onSuccess?: () => void;
}

export const RegionalPaymentModal: React.FC<RegionalPaymentModalProps> = ({
  isOpen,
  onClose,
  agentId = 'general',
  agentName,
  region,
  jurisdiction,
  language = 'pl',
  currency,
  defaultPrice,
  initialPrice,
  defaultSinglePrice,
  initialSinglePrice,
  userEmail,
  currentUserEmail,
  onSuccess
}) => {
  const effectiveRegion: Jurisdiction = region || jurisdiction || 'PL';
  const effectivePrice = defaultPrice || initialPrice;
  const effectiveSinglePrice = defaultSinglePrice || initialSinglePrice;

  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const isFr = language === 'fr';
  const isDe = language === 'de';
  const isIt = language === 'it';
  const isEs = language === 'es';
  const isPt = language === 'pt';
  const isSv = language === 'sv';

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'single' | 'hub_pass'>('monthly');
  const [paymentMethod, setPaymentMethod] = useState<string>(() => {
    if (effectiveRegion === 'PL') return 'blik';
    if (effectiveRegion === 'SE') return 'swish';
    if (effectiveRegion === 'ES') return 'bizum';
    if (effectiveRegion === 'PT') return 'mbway';
    if (['EU', 'DE', 'FR', 'IT', 'US'].includes(effectiveRegion)) return 'stripe';
    return 'monobank';
  });
  const [blikCode, setBlikCode] = useState('');
  const [needInvoice, setNeedInvoice] = useState(false);
  const [companyTaxId, setCompanyTaxId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  // Compute pricing based on 98% gross margin rule, currency & region
  const getPrices = () => {
    const activeCurr = currency || (
      effectiveRegion === 'US' ? 'USD' :
      effectiveRegion === 'PL' ? 'PLN' :
      effectiveRegion === 'SE' ? 'SEK' :
      ['DE', 'FR', 'IT', 'ES', 'PT', 'EU'].includes(effectiveRegion) ? 'EUR' : 'UAH'
    );

    if (activeCurr === 'SEK' || effectiveRegion === 'SE') {
      const monthly = effectivePrice ? Math.round(effectivePrice * 11.5) : 990;
      const single = effectiveSinglePrice ? Math.round(effectiveSinglePrice * 11.5) : 150;
      const hubPass = 4900;
      return { currency: 'SEK', currencySymbol: 'kr SEK', monthly, single, hubPass };
    }
    if (activeCurr === 'USD' || effectiveRegion === 'US') {
      const monthly = effectivePrice || 99;
      const single = effectiveSinglePrice || 15;
      const hubPass = 399;
      return { currency: 'USD', currencySymbol: '$', monthly, single, hubPass };
    }
    if (activeCurr === 'PLN' || effectiveRegion === 'PL') {
      const monthly = effectivePrice || 349;
      const single = effectiveSinglePrice || 35;
      const hubPass = 1490;
      return { currency: 'PLN', currencySymbol: 'zł PLN', monthly, single, hubPass };
    }
    if (activeCurr === 'EUR' || ['DE', 'FR', 'IT', 'ES', 'PT', 'EU'].includes(effectiveRegion)) {
      const monthly = effectivePrice || 99;
      const single = effectiveSinglePrice || 15;
      const hubPass = 399;
      return { currency: 'EUR', currencySymbol: '€ EUR', monthly, single, hubPass };
    }
    // Ukraine UAH
    const monthly = effectivePrice || 2450;
    const single = effectiveSinglePrice || 150;
    const hubPass = 14900;
    return { currency: 'UAH', currencySymbol: 'грн', monthly, single, hubPass };
  };

  const prices = getPrices();
  const currentAmount =
    selectedPlan === 'monthly'
      ? prices.monthly
      : selectedPlan === 'single'
      ? prices.single
      : prices.hubPass;

  const currentRequests =
    selectedPlan === 'monthly'
      ? (isDe ? '30 Anfragen / 30 Tage' : isFr ? '30 requêtes / 30 jours' : isIt ? '30 richieste / 30 giorni' : isEs ? '30 consultas / 30 días' : isPt ? '30 pedidos / 30 dias' : isSv ? '30 förfrågningar / 30 dagar' : isPl ? '30 zapytań / 30 dni' : isUk ? '30 запитів / 30 днів' : '30 requests / 30 days')
      : selectedPlan === 'single'
      ? (isDe ? '1 Einzelabfrage' : isFr ? '1 requête unitaire' : isIt ? '1 singola richiesta' : isEs ? '1 consulta individual' : isPt ? '1 consulta individual' : isSv ? '1 enstaka körning' : isPl ? '1 zapytanie' : isUk ? '1 запуск' : '1 single run')
      : (isDe ? '150 Anfragen (Alle Hub-Module)' : isFr ? '150 requêtes (Tous les Modules)' : isIt ? '150 richieste (Tutti i Moduli Hub)' : isEs ? '150 consultas (Todos los Módulos)' : isPt ? '150 consultas (Todos os Módulos)' : isSv ? '150 förfrågningar (Hela Hubben)' : isPl ? '150 zapytań (Wszystkie Moduły)' : isUk ? '150 запитів (Усі Модулі Хабу)' : '150 requests (All Hub Modules)');

  const handlePay = async () => {
    setIsProcessing(true);
    setErrorMessage(null);

    if (paymentMethod === 'blik' && (!blikCode || blikCode.length !== 6)) {
      setErrorMessage(
        isPl ? 'Proszę wprowadzić prawidłowy 6-cyfrowy kod BLIK' : isUk ? 'Будь ласка, введіть коректний 6-значний код BLIK' : 'Please enter a valid 6-digit BLIK code'
      );
      setIsProcessing(false);
      return;
    }

    try {
      const payload = {
        agentId,
        selectedPlan,
        currency: prices.currency,
        amount: currentAmount,
        paymentMethod,
        blikCode: paymentMethod === 'blik' ? blikCode : undefined,
        invoice: needInvoice ? { taxId: companyTaxId, companyName } : undefined
      };

      const res = await fetch('/api/subscription/regional-pay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok && data.success) {
        if (data.pageUrl) {
          window.location.href = data.pageUrl;
        } else {
          setPaymentSuccess(true);
          setTimeout(() => {
            if (onSuccess) onSuccess();
            onClose();
          }, 2500);
        }
      } else {
        setErrorMessage(data.error || (isPl ? 'Błąd przetwarzania płatności' : isUk ? 'Помилка обробки платежу' : 'Payment processing error'));
      }
    } catch (err: any) {
      console.error('Payment invocation error:', err);
      setErrorMessage(
        isPl ? 'Błąd połączenia z bramką płatności' : isUk ? "Помилка зв'язку з платіжним шлюзом" : 'Payment gateway connection error'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const getRegionBadge = () => {
    switch (effectiveRegion) {
      case 'SE': return '🇸🇪 SVERIGE • SEK';
      case 'IT': return '🇮🇹 ITALIA • EUR';
      case 'ES': return '🇪🇸 ESPAÑA • EUR';
      case 'PT': return '🇵🇹 PORTUGAL • EUR';
      case 'DE': return '🇩🇪 DEUTSCHLAND • EUR';
      case 'FR': return '🇫🇷 FRANCE • EUR';
      case 'PL': return '🇵🇱 POLSKA • PLN';
      case 'US': return '🇺🇸 USA • USD';
      case 'EU': return '🇪🇺 EUROPEAN UNION • EUR';
      default: return '🇺🇦 УКРАЇНА • UAH';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in font-mono">
      <div className="relative w-full max-w-xl bg-white border border-slate-200 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header with Jurisdictional Badge */}
        <div className="p-6 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center text-rose-400">
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase rounded bg-rose-500/20 text-rose-300 border border-rose-400/30">
                  {getRegionBadge()}
                </span>
                <span className="text-[10px] text-emerald-400 font-bold flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  {isDe ? 'Sichere Zahlung' : isFr ? 'Paiement Sécurisé' : isIt ? 'Pagamento Sicuro' : isEs ? 'Pago Seguro' : isPt ? 'Pagamento Seguro' : isSv ? 'Säker Betalning' : isPl ? 'Bezpieczna Płatność' : isUk ? 'Захищений Платіж' : 'SSL Encrypted'}
                </span>
              </div>
              <h2 className="text-base font-extrabold uppercase tracking-tight text-white mt-1 line-clamp-1">
                {agentName || (isDe ? 'KI-Modul-Abonnement' : isFr ? 'Abonnement Module IA' : isIt ? 'Abbonamento Modulo IA' : isEs ? 'Suscripción al Módulo IA' : isPt ? 'Subscrição do Módulo IA' : isSv ? 'Prenumeration AI-Modul' : isPl ? 'Subskrypcja Modułu AI' : isUk ? 'Підписка на ШІ-Модуль' : 'AI Module Subscription')}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-slate-900">
          {paymentSuccess ? (
            <div className="p-8 text-center space-y-4 my-auto">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full mx-auto flex items-center justify-center animate-bounce">
                <Check className="w-8 h-8 stroke-[3]" />
              </div>
              <h3 className="text-lg font-black text-slate-900 uppercase">
                {isDe ? '🎉 ZAHLUNG ERFOLGREICH ABGESCHLOSSEN!' : isFr ? '🎉 PAIEMENT EFFECTUÉ AVEC SUCCÈS !' : isIt ? '🎉 PAGAMENTO EFFETTUATO CON SUCCESSO!' : isEs ? '🎉 ¡PAGO REALIZADO CON ÉXITO!' : isPt ? '🎉 PAGAMENTO EFETUADO COM SUCESSO!' : isSv ? '🎉 BETALNINGEN GENOMFÖRD!' : isPl ? '🎉 PŁATNOŚĆ PRZYJĘTA POMYŚLNIE!' : isUk ? '🎉 ОПЛАТУ УСПІШНО ПРИЙНЯТО!' : '🎉 PAYMENT SUCCESSFUL!'}
              </h3>
              <p className="text-xs text-slate-600 font-mono">
                {isDe
                  ? 'Der Zugriff auf das Modul und Ihr Abfrageguthaben wurden sofort freigeschaltet.'
                  : isFr
                  ? 'L\'accès au module et votre solde de requêtes ont été activés instantanément.'
                  : isIt
                  ? 'L\'accesso al modulo e il saldo delle richieste sono stati attivati immediatamente.'
                  : isEs
                  ? 'El acceso al módulo y el saldo de consultas se han activado instantáneamente.'
                  : isPt
                  ? 'O acesso ao módulo e o saldo de consultas foram ativados imediatamente.'
                  : isSv
                  ? 'Åtkomst till modulen och ditt förfrågningssaldo har aktiverats direkt.'
                  : isPl
                  ? 'Dostęp do modułu oraz pakiet zapytań zostały natychmiast aktywowane.'
                  : isUk
                  ? 'Доступ до модуля та пакет запитів активовано миттєво.'
                  : 'Your module access and request balance have been activated instantly.'}
              </p>
            </div>
          ) : (
            <>
              {/* Step 1: Select Plan Tier */}
              <div className="space-y-2.5">
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-700 flex items-center justify-between">
                  <span>1. {isDe ? 'Tarif wählen' : isFr ? 'Choisir le Forfait' : isIt ? 'Seleziona Tariffa' : isEs ? 'Seleccione Tarifa' : isPt ? 'Selecione o Plano' : isSv ? 'Välj Prisplan' : isPl ? 'Wybierz Pakiet / Taryfę' : isUk ? 'Оберіть Пакет / Тариф' : 'Select Plan Tier'}</span>
                  <span className="text-emerald-600 font-bold text-[10px]">
                    {isDe ? '💎 98%+ Betrieblicher Nutzen' : isFr ? '💎 98%+ Valeur Entreprise' : isIt ? '💎 98%+ Valore Aziendale' : isEs ? '💎 98%+ Valor Empresarial' : isPt ? '💎 98%+ Valor Empresarial' : isSv ? '💎 98%+ Verksamhetsnytta' : isPl ? '💎 98%+ Wartości Biznesowej' : isUk ? '💎 98%+ Економія часу' : '💎 Enterprise Value'}
                  </span>
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Plan Option 1: Single Run */}
                  <div
                    onClick={() => setSelectedPlan('single')}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between ${
                      selectedPlan === 'single'
                        ? 'border-blue-600 bg-blue-50/60 shadow-xs'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-extrabold text-blue-600 uppercase">⚡ {isDe ? 'Einzeln' : isFr ? 'Unitaire' : isIt ? 'Singola' : isEs ? 'Única' : isPt ? 'Única' : isSv ? 'Engångs' : isPl ? 'Pojedynczo' : isUk ? 'Разово' : 'Single'}</span>
                        {selectedPlan === 'single' && <Check className="w-4 h-4 text-blue-600" />}
                      </div>
                      <div className="text-lg font-black text-slate-900">
                        {prices.currency === 'USD' ? `$${prices.single}` : prices.currency === 'EUR' ? `€${prices.single}` : `${prices.single} ${prices.currencySymbol}`}
                      </div>
                      <p className="text-[10px] text-slate-500 leading-tight">
                        {isDe ? '1 Einzelabfrage' : isFr ? '1 requête' : isIt ? '1 singola analisi' : isEs ? '1 consulta' : isPt ? '1 execução' : isSv ? '1 körning' : isPl ? '1 uruchomienie' : isUk ? '1 повний запуск' : '1 single run'}
                      </p>
                    </div>
                  </div>

                  {/* Plan Option 2: Monthly 30 requests (Recommended) */}
                  <div
                    onClick={() => setSelectedPlan('monthly')}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between relative ${
                      selectedPlan === 'monthly'
                        ? 'border-rose-600 bg-rose-50/50 shadow-md ring-2 ring-rose-500/20'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className="absolute -top-2.5 right-3 bg-rose-600 text-white text-[8px] font-extrabold uppercase px-2 py-0.5 rounded-full shadow-xs">
                      {isDe ? 'TOP • Empfohlen' : isFr ? 'TOP • Recommandé' : isIt ? 'TOP • Consigliato' : isEs ? 'TOP • Recomendado' : isPt ? 'TOP • Recomendado' : isSv ? 'TOP • Rekommenderad' : isPl ? 'HIT • Polecane' : isUk ? 'ТОП • Рекомендовано' : 'POPULAR'}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-extrabold text-rose-600 uppercase">💎 {isDe ? 'Monatlich' : isFr ? 'Mensuel' : isIt ? 'Mensile' : isEs ? 'Mensual' : isPt ? 'Mensal' : isSv ? 'Månadsvis' : isPl ? 'Miesiąc' : isUk ? 'Місяць' : 'Monthly'}</span>
                        {selectedPlan === 'monthly' && <Check className="w-4 h-4 text-rose-600" />}
                      </div>
                      <div className="text-lg font-black text-slate-900">
                        {prices.currency === 'USD' ? `$${prices.monthly}` : prices.currency === 'EUR' ? `€${prices.monthly}` : `${prices.monthly} ${prices.currencySymbol}`}
                      </div>
                      <p className="text-[10px] text-slate-500 leading-tight">
                        {isDe ? '30 Anfragen / Monat' : isFr ? '30 requêtes / mois' : isIt ? '30 richieste / mese' : isEs ? '30 consultas / mes' : isPt ? '30 consultas / mês' : isSv ? '30 förfrågningar / mån' : isPl ? '30 zapytań / 30 dni' : isUk ? '30 запитів / 30 днів' : '30 requests / month'}
                      </p>
                    </div>
                  </div>

                  {/* Plan Option 3: Hub Pass */}
                  <div
                    onClick={() => setSelectedPlan('hub_pass')}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between ${
                      selectedPlan === 'hub_pass'
                        ? 'border-amber-600 bg-amber-50/60 shadow-xs'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-extrabold text-amber-600 uppercase">👑 Hub Pass</span>
                        {selectedPlan === 'hub_pass' && <Check className="w-4 h-4 text-amber-600" />}
                      </div>
                      <div className="text-lg font-black text-slate-900">
                        {prices.currency === 'USD' ? `$${prices.hubPass}` : prices.currency === 'EUR' ? `€${prices.hubPass}` : `${prices.hubPass} ${prices.currencySymbol}`}
                      </div>
                      <p className="text-[10px] text-slate-500 leading-tight">
                        {isDe ? '150 Anfragen auf alle Module' : isFr ? '150 requêtes sur tout le Hub' : isIt ? '150 richieste per l\'intero Hub' : isEs ? '150 consultas para todo el Hub' : isPt ? '150 consultas para todo o Hub' : isSv ? '150 förfrågningar hela hubben' : isPl ? '150 zapytań na cały hub' : isUk ? '150 запитів на весь хаб' : 'All-access Hub Pass'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: Payment Method */}
              <div className="space-y-2.5">
                <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-700">
                  2. {isDe ? 'Zahlungsmethode' : isFr ? 'Méthode de Paiement' : isIt ? 'Metodo di Pagamento' : isEs ? 'Método de Pago' : isPt ? 'Método de Pagamento' : isSv ? 'Betalningsmetod' : isPl ? 'Metoda Płatności' : isUk ? 'Спосіб Оплати' : 'Payment Method'}
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {effectiveRegion === 'SE' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('swish')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'swish'
                            ? 'border-emerald-600 bg-emerald-50/80 text-emerald-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">📲</span>
                        <div className="text-xs">
                          <div className="font-extrabold">Swish / BankID</div>
                          <div className="text-[9px] text-slate-500">Mobil betalning</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('klarna')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'klarna'
                            ? 'border-pink-600 bg-pink-50/80 text-pink-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">🛍️</span>
                        <div className="text-xs">
                          <div className="font-extrabold">Klarna / Kort</div>
                          <div className="text-[9px] text-slate-500">Visa / Mastercard</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('wallet')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'wallet'
                            ? 'border-blue-600 bg-blue-50/80 text-blue-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">🍏</span>
                        <div className="text-xs">
                          <div className="font-extrabold">Apple / Google Pay</div>
                          <div className="text-[9px] text-slate-500">Snabbkassa</div>
                        </div>
                      </button>
                    </>
                  ) : effectiveRegion === 'PL' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('blik')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'blik'
                            ? 'border-rose-600 bg-rose-50/80 text-rose-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">📲</span>
                        <div className="text-xs">
                          <div className="font-extrabold">BLIK</div>
                          <div className="text-[9px] text-slate-500">{isPl ? 'Kod 6 cyfr' : '6-digit code'}</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('p24')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'p24'
                            ? 'border-rose-600 bg-rose-50/80 text-rose-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">💳</span>
                        <div className="text-xs">
                          <div className="font-extrabold">Przelewy24 / PayU</div>
                          <div className="text-[9px] text-slate-500">Bank / Karta</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('card')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'card'
                            ? 'border-rose-600 bg-rose-50/80 text-rose-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">🍏</span>
                        <div className="text-xs">
                          <div className="font-extrabold">Apple / Google Pay</div>
                          <div className="text-[9px] text-slate-500">Karty Visa/MC</div>
                        </div>
                      </button>
                    </>
                  ) : ['EU', 'DE', 'FR', 'IT', 'ES', 'PT', 'US'].includes(effectiveRegion) ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('stripe')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'stripe'
                            ? 'border-blue-600 bg-blue-50/80 text-blue-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">💳</span>
                        <div className="text-xs">
                          <div className="font-extrabold">{isDe ? 'Kreditkarte (Stripe)' : isFr ? 'Carte Bancaire' : isIt ? 'Carta di Credito' : isEs ? 'Tarjeta de Crédito' : isPt ? 'Cartão de Crédito' : 'Stripe Card'}</div>
                          <div className="text-[9px] text-slate-500">Visa / Mastercard / Amex</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('sepa')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'sepa'
                            ? 'border-blue-600 bg-blue-50/80 text-blue-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">🏦</span>
                        <div className="text-xs">
                          <div className="font-extrabold">{isDe ? 'SEPA-Überweisung' : isFr ? 'Virement SEPA' : isIt ? 'Bonifico SEPA' : isEs ? 'Transferencia SEPA' : isPt ? 'Transferência SEPA' : 'SEPA Transfer'}</div>
                          <div className="text-[9px] text-slate-500">{effectiveRegion === 'US' ? 'ACH Direct Debit' : 'EU Bank Wire'}</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('wallet')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'wallet'
                            ? 'border-blue-600 bg-blue-50/80 text-blue-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">🍏</span>
                        <div className="text-xs">
                          <div className="font-extrabold">Apple / Google Pay</div>
                          <div className="text-[9px] text-slate-500">1-click checkout</div>
                        </div>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('monobank')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'monobank'
                            ? 'border-emerald-600 bg-emerald-50/80 text-emerald-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">🐱</span>
                        <div className="text-xs">
                          <div className="font-extrabold">Monobank</div>
                          <div className="text-[9px] text-slate-500">Еквайринг & MonoPay</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('card')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'card'
                            ? 'border-emerald-600 bg-emerald-50/80 text-emerald-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">💳</span>
                        <div className="text-xs">
                          <div className="font-extrabold">Visa / Mastercard</div>
                          <div className="text-[9px] text-slate-500">Будь-яка картка</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('wallet')}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                          paymentMethod === 'wallet'
                            ? 'border-emerald-600 bg-emerald-50/80 text-emerald-900 font-bold shadow-xs'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="text-base">🍏</span>
                        <div className="text-xs">
                          <div className="font-extrabold">Apple / Google Pay</div>
                          <div className="text-[9px] text-slate-500">Миттєва оплата</div>
                        </div>
                      </button>
                    </>
                  )}
                </div>

                {/* If BLIK selected, show 6-digit input */}
                {paymentMethod === 'blik' && (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2 mt-2">
                    <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                      <span>{isPl ? 'Wprowadź 6-cyfrowy kod BLIK z aplikacji banku:' : 'Введіть 6-значний код BLIK:'}</span>
                      <span className="text-[10px] text-slate-500 font-normal">{isPl ? 'Ważny przez 2 minuty' : 'Діє 2 хвилини'}</span>
                    </label>
                    <input
                      type="text"
                      maxLength={6}
                      value={blikCode}
                      onChange={(e) => setBlikCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="• • • • • •"
                      className="w-full text-center text-xl tracking-[0.5em] font-black py-2.5 bg-white border-2 border-rose-300 focus:border-rose-600 rounded-xl text-slate-900 outline-hidden"
                    />
                  </div>
                )}
              </div>

              {/* Step 3: B2B Invoice Option */}
              <div className="pt-2 border-t border-slate-100 space-y-3">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={needInvoice}
                    onChange={(e) => setNeedInvoice(e.target.checked)}
                    className="w-4 h-4 text-slate-900 rounded border-slate-300 focus:ring-slate-900 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-slate-700">
                    {isDe
                      ? '🏢 B2B-Rechnung mit USt-IdNr anfordern (§ 14 UStG)'
                      : isFr
                      ? '🏢 Demander une Facture B2B avec numéro TVA (SIRET)'
                      : isIt
                      ? '🏢 Richiedi Fattura Elettronica B2B (P.IVA / SDI)'
                      : isEs
                      ? '🏢 Solicitar Factura B2B con NIF/CIF (FacturaE)'
                      : isPt
                      ? '🏢 Solicitar Fatura com NIF (SAF-T / ATCUD)'
                      : isSv
                      ? '🏢 Begär Företagsfaktura / Moms (Org.nr)'
                      : isPl
                      ? '🏢 Chcę otrzymać Fakturę VAT 23% na firmę (NIP)'
                      : isUk
                      ? '🏢 Бажаю отримати офіційний рахунок/фактуру для юрособи (ЄДРПОУ)'
                      : '🏢 Request B2B Corporate VAT Invoice'}
                  </span>
                </label>

                {needInvoice && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200 text-xs animate-fade-in">
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase">
                        {isDe ? 'USt-IdNr / Steuernummer' : isFr ? 'Numéro SIREN / SIRET' : isIt ? 'Partita IVA / Codice Fiscale' : isEs ? 'NIF / CIF' : isPt ? 'NIF / NIPC' : isSv ? 'Org.nr / Momsnummer' : isPl ? 'NIP / Identyfikator Podatkowy' : isUk ? 'Код ЄДРПОУ / ІПН' : 'Tax ID / VAT Number'}
                      </label>
                      <input
                        type="text"
                        value={companyTaxId}
                        onChange={(e) => setCompanyTaxId(e.target.value)}
                        placeholder={effectiveRegion === 'PL' ? 'PL 1234567890' : effectiveRegion === 'DE' ? 'DE 123456789' : effectiveRegion === 'FR' ? 'FR 12345678901' : effectiveRegion === 'IT' ? 'IT 12345678901' : effectiveRegion === 'ES' ? 'ES B12345678' : effectiveRegion === 'PT' ? 'PT 501234567' : effectiveRegion === 'SE' ? 'SE 556123456701' : 'Tax ID'}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-mono text-slate-900 mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase">
                        {isDe ? 'Firmenname' : isFr ? 'Raison Sociale' : isIt ? 'Ragione Sociale' : isEs ? 'Razón Social' : isPt ? 'Denominação Social' : isSv ? 'Företagsnamn' : isPl ? 'Pełna Nazwa Firmy' : isUk ? 'Повна Назва Підприємства' : 'Company Name'}
                      </label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Company Name"
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-mono text-slate-900 mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Error Alert */}
              {errorMessage && (
                <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                  <span>{errorMessage}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        {!paymentSuccess && (
          <div className="p-5 bg-slate-50 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="text-left">
              <div className="text-[10px] text-slate-500 uppercase font-bold">
                {isDe ? 'Zu zahlender Betrag:' : isFr ? 'Montant Total TTC :' : isIt ? 'Totale da pagare:' : isEs ? 'Total a pagar:' : isPt ? 'Total a pagar:' : isSv ? 'Totalt att betala:' : isPl ? 'Do zapłaty brutto:' : isUk ? 'До сплати разом:' : 'Total Amount:'}
              </div>
              <div className="text-xl font-black text-slate-900">
                {prices.currency === 'USD' ? `$${currentAmount}` : prices.currency === 'EUR' ? `€${currentAmount}` : `${currentAmount} ${prices.currencySymbol}`}
                <span className="text-[10px] text-slate-500 font-normal ml-1.5 font-sans">
                  ({currentRequests})
                </span>
              </div>
            </div>

            <button
              onClick={handlePay}
              disabled={isProcessing}
              className="px-6 py-3.5 bg-slate-900 hover:bg-rose-600 text-white font-black text-xs uppercase rounded-xl transition-all shadow-lg hover:shadow-rose-600/30 flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin text-white" />
                  <span>{isDe ? 'Wird bearbeitet...' : isFr ? 'Traitement...' : isIt ? 'Elaborazione...' : isEs ? 'Procesando...' : isPt ? 'A processar...' : isSv ? 'Behandlar...' : isPl ? 'Przetwarzanie...' : isUk ? 'Обробка...' : 'Processing...'}</span>
                </>
              ) : (
                <>
                  <span>
                    {isDe
                      ? `💳 Jetzt bezahlen (${prices.currency === 'EUR' ? '€' : ''}${currentAmount})`
                      : isFr
                      ? `💳 Payer ${prices.currency === 'EUR' ? '€' : ''}${currentAmount}`
                      : isIt
                      ? `💳 Paga ${prices.currency === 'EUR' ? '€' : ''}${currentAmount}`
                      : isEs
                      ? `💳 Pagar ${prices.currency === 'EUR' ? '€' : ''}${currentAmount}`
                      : isPt
                      ? `💳 Pagar ${prices.currency === 'EUR' ? '€' : ''}${currentAmount}`
                      : isSv
                      ? `💳 Betala ${currentAmount} kr`
                      : isPl
                      ? `💳 Zapłać ${currentAmount} ${prices.currencySymbol}`
                      : isUk
                      ? `💳 Сплатити ${currentAmount} ${prices.currencySymbol}`
                      : `💳 Pay ${prices.currency === 'USD' ? '$' : ''}${currentAmount}`}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
